if (!customElements.get("sticky-product-atc")) {
  customElements.define(
    "sticky-product-atc",
    class StickyProductAtc extends HTMLElement {
      connectedCallback() {
        this.section = document.getElementById(
          `MainProduct-${this.dataset.section}`,
        );
        this.mainButton = this.section?.querySelector(
          ".product-form__submit[name='add']",
        );
        this.mainPrice = this.section?.querySelector(
          `#price-${this.dataset.section}`,
        );
        this.button = this.querySelector("[data-sticky-product-atc-button]");
        this.label = this.querySelector("[data-sticky-product-atc-label]");
        this.price = this.querySelector("[data-sticky-product-atc-price]");
        this.variant = this.querySelector("[data-sticky-product-atc-variant]");
        this.mainButtonPrice = this.section?.querySelector(
          `#ProductSubmitPrice-${this.dataset.section}`,
        );
        this.quantity = this.querySelector(
          "[data-sticky-product-atc-quantity]",
        );
        this.variantInput = this.section?.querySelector(
          `#product-form-${this.dataset.section} input[name="id"]`,
        );
        this.quantityInput = this.section?.querySelector(
          `#Quantity-${this.dataset.section}`,
        );

        const variantData = this.querySelector(
          "[data-sticky-product-atc-variants]",
        );
        this.variants = variantData ? JSON.parse(variantData.textContent) : [];

        if (!this.mainButton || !this.button) {
          this.hidden = true;
          return;
        }

        this.addToBagLabel = "Add to Bag";
        this.ticking = false;

        this.handleClick = () => {
          if (
            this.mainButton.disabled ||
            this.mainButton.getAttribute("aria-disabled") === "true"
          ) {
            return;
          }

          this.mainButton.click();
        };

        this.handleScroll = () => {
          if (this.ticking) return;

          this.ticking = true;
          window.requestAnimationFrame(() => {
            this.updateVisibility();
            this.ticking = false;
          });
        };

        this.handleVariantChange = () => {
          this.syncState();
        };

        this.handleQuantityChange = () => {
          this.syncState();
        };

        this.syncState = () => {
          const isUnavailable = this.mainButton.disabled;
          const isLoading =
            this.mainButton.getAttribute("aria-disabled") === "true" ||
            this.mainButton.classList.contains("loading");

          this.button.disabled = isUnavailable || isLoading;
          this.button.classList.toggle("is-loading", isLoading && !isUnavailable);

          if (this.label) {
            const mainLabel = this.mainButton
              .querySelector("span:not(.hidden)")
              ?.textContent?.trim();

            this.label.textContent = isUnavailable
              ? mainLabel || "Sold out"
              : isLoading
                ? "Adding…"
                : this.addToBagLabel;
          }

          this.syncVariant();
          this.syncPrice();
        };

        this.button.addEventListener("click", this.handleClick);
        this.variantInput?.addEventListener("change", this.handleVariantChange);
        this.quantityInput?.addEventListener(
          "change",
          this.handleQuantityChange,
        );
        this.quantityInput?.addEventListener(
          "input",
          this.handleQuantityChange,
        );
        window.addEventListener("scroll", this.handleScroll, { passive: true });
        window.addEventListener("resize", this.handleScroll);

        this.buttonObserver = new MutationObserver(this.syncState);
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ["disabled", "aria-disabled", "class"],
        });

        if (this.mainPrice) {
          this.priceObserver = new MutationObserver(this.syncState);
          this.priceObserver.observe(this.mainPrice, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.syncState();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.button?.removeEventListener("click", this.handleClick);
        window.removeEventListener("scroll", this.handleScroll);
        window.removeEventListener("resize", this.handleScroll);
        this.variantInput?.removeEventListener(
          "change",
          this.handleVariantChange,
        );
        this.quantityInput?.removeEventListener(
          "change",
          this.handleQuantityChange,
        );
        this.quantityInput?.removeEventListener(
          "input",
          this.handleQuantityChange,
        );
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();
      }

      updateVisibility() {
        const shouldShow = this.mainButton.getBoundingClientRect().bottom <= 0;

        this.classList.toggle("is-visible", shouldShow);
        this.setAttribute("aria-hidden", String(!shouldShow));
        this.button.tabIndex = shouldShow ? 0 : -1;
      }

      syncVariant() {
        const selectedVariant = this.getSelectedVariant();

        if (this.variant && selectedVariant) {
          this.variant.textContent = selectedVariant.title;
        }

        const quantity = this.getQuantity();

        if (this.quantity) {
          this.quantity.textContent = `×${quantity}`;
          this.quantity.hidden = quantity === 1;
        }
      }

      syncPrice() {
        const sourcePrice = this.mainPrice?.querySelector(".price");
        const targetPrice = this.price?.querySelector(".price");

        if (sourcePrice && targetPrice) {
          targetPrice.className = sourcePrice.className;
          targetPrice.innerHTML = sourcePrice.innerHTML;
        }

        const selectedVariant = this.getSelectedVariant();

        if (!selectedVariant || !targetPrice) return;

        const quantity = this.getQuantity();
        const totalPrice = selectedVariant.price * quantity;
        const totalCompareAtPrice =
          selectedVariant.compare_at_price &&
          selectedVariant.compare_at_price > selectedVariant.price
            ? selectedVariant.compare_at_price * quantity
            : null;

        const regularPrice = targetPrice.querySelector(
          ".price__regular .price-item--regular",
        );
        const salePrice = targetPrice.querySelector(
          ".price__sale .price-item--sale",
        );
        const compareAtPrice = targetPrice.querySelector(
          ".price__sale .price-item--regular",
        );

        this.updatePriceElements(
          { regularPrice, salePrice, compareAtPrice },
          totalPrice,
          totalCompareAtPrice,
        );

        const mainButtonPrice = this.mainButtonPrice?.querySelector(".price");

        if (mainButtonPrice) {
          this.updatePriceElements(
            {
              regularPrice: mainButtonPrice.querySelector(
                ".price__regular .price-item--regular",
              ),
              salePrice: mainButtonPrice.querySelector(
                ".price__sale .price-item--sale",
              ),
              compareAtPrice: mainButtonPrice.querySelector(
                ".price__sale .price-item--regular",
              ),
            },
            totalPrice,
            totalCompareAtPrice,
          );
        }
      }

      updatePriceElements(
        { regularPrice, salePrice, compareAtPrice },
        totalPrice,
        totalCompareAtPrice,
      ) {
        if (regularPrice) regularPrice.textContent = this.formatMoney(totalPrice);
        if (salePrice) salePrice.textContent = this.formatMoney(totalPrice);
        if (compareAtPrice && totalCompareAtPrice) {
          compareAtPrice.textContent = this.formatMoney(totalCompareAtPrice);
        }
      }

      getSelectedVariant() {
        return this.variants.find(
          (variant) => String(variant.id) === this.variantInput?.value,
        );
      }

      getQuantity() {
        return Math.max(1, Number.parseInt(this.quantityInput?.value, 10) || 1);
      }

      formatMoney(cents) {
        const moneyFormat = this.dataset.moneyFormat || "£{{amount}}";
        const placeholder = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);

        if (!placeholder) return (cents / 100).toFixed(2);

        const formatWithDelimiters = (
          amount,
          precision = 2,
          thousands = ",",
          decimal = ".",
        ) => {
          const number = (amount / 100).toFixed(precision);
          const parts = number.split(".");

          parts[0] = parts[0].replace(
            /(\d)(?=(\d{3})+(?!\d))/g,
            `$1${thousands}`,
          );

          return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
        };

        const formattedAmount = {
          amount: formatWithDelimiters(cents),
          amount_no_decimals: formatWithDelimiters(cents, 0),
          amount_with_comma_separator: formatWithDelimiters(
            cents,
            2,
            ".",
            ",",
          ),
          amount_no_decimals_with_comma_separator: formatWithDelimiters(
            cents,
            0,
            ".",
            ",",
          ),
          amount_with_apostrophe_separator: formatWithDelimiters(
            cents,
            2,
            "'",
            ".",
          ),
        }[placeholder[1]];

        return moneyFormat.replace(
          placeholder[0],
          formattedAmount || formatWithDelimiters(cents),
        );
      }
    },
  );
}
